import axios from 'axios';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — skip redirect on auth pages so login errors show properly
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const publicAuthPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/invite'];
      const isPublicAuthPage = publicAuthPaths.some(p => window.location.pathname.startsWith(p));
      if (!isPublicAuthPage) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } catch {}
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  adminLogin: (data) => api.post('/auth/admin-login', data),
  register: (data) => api.post('/auth/register', data),
  googleLogin: (idToken) => api.post('/auth/google', { idToken }),
  getMe: () => api.get('/auth/me'),
  revokeAllSessions: () => api.post('/auth/revoke-all-sessions'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (token, data) => api.post(`/auth/reset-password/${token}`, data),
  verifyEmail: (token) => api.get(`/auth/verify-email/${token}`),
  setup2FA: () => api.post('/auth/2fa/setup'),
  verify2FASetup: (token) => api.post('/auth/2fa/verify-setup', { token }),
  disable2FA: (token) => api.post('/auth/2fa/disable', { token }),
  verify2FALogin: (tempToken, token) => api.post('/auth/2fa/verify', { tempToken, token }),
};

// Calendars
export const calendarAPI = {
  getAll: () => api.get('/calendars'),
  getOne: (id) => api.get(`/calendars/${id}`),
  getPublic: (slug) => api.get(`/calendars/public/${slug}`),
  create: (data) => api.post('/calendars', data),
  update: (id, data) => api.put(`/calendars/${id}`, data),
  delete: (id) => api.delete(`/calendars/${id}`),
  getAvailableSlots: (id, date) => api.get(`/calendars/${id}/available-slots`, { params: { date } }),
  getStats: () => api.get('/calendars/stats'),
};

// Bookings
export const bookingAPI = {
  getAll: (params) => api.get('/bookings', { params }),
  getStats: () => api.get('/bookings/stats'),
  create: (data) => api.post('/bookings', data),
  cancel: (id, reason) => api.put(`/bookings/${id}/cancel`, { reason }),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status`, { status }),
  manage: (token) => api.get(`/bookings/manage/${token}`),
  selfCancel: (token, reason) => api.post(`/bookings/cancel/${token}`, { reason }),
};

// Payments
export const paymentAPI = {
  createCheckout: (data) => api.post('/payments/create-checkout', data),
  retrieveSession: (sessionId) => api.get(`/payments/session/${sessionId}`),
};

// Transactions
export const transactionAPI = {
  getAll: (params) => api.get('/transactions', { params }),
};

// Waitlist
export const waitlistAPI = {
  join: (data) => api.post('/waitlist', data),
};

// Reviews
export const reviewAPI = {
  submit: (data) => api.post('/reviews', data),
  getForCalendar: (calendarId, params) => api.get(`/reviews/calendar/${calendarId}`, { params }),
  getMy: () => api.get('/reviews/my'),
};

// Packages
export const packageAPI = {
  getForCalendar: (calendarId) => api.get(`/packages/calendar/${calendarId}`),
  getMy: () => api.get('/packages/my'),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
  purchase: (data) => api.post('/packages/purchase', data),
  getMyPurchases: (email) => api.get('/packages/my-purchases', { params: { email } }),
  redeem: (data) => api.post('/packages/redeem', data),
};

// Promo Codes
export const promoCodeAPI = {
  create: (data) => api.post('/promo-codes', data),
  getMy: () => api.get('/promo-codes/my'),
  validate: (data) => api.post('/promo-codes/validate', data),
  apply: (data) => api.post('/promo-codes/apply', data),
  delete: (id) => api.delete(`/promo-codes/${id}`),
};

// Analytics
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getCalendar: (calendarId) => api.get(`/analytics/calendar/${calendarId}`),
  getChurnPrediction: () => api.get('/analytics/churn-prediction'),
  getCohortRetention: () => api.get('/analytics/cohort-retention'),
  getStats: () => api.get('/analytics/stats'),
};

// Integrations
export const integrationsAPI = {
  getAll: () => api.get('/integrations'),
  getStats: () => api.get('/integrations/stats'),
  connect: (id) => api.post(`/integrations/${id}/connect`),
  disconnect: (id) => api.post(`/integrations/${id}/disconnect`),
};

// Recurring Bookings
export const recurringBookingAPI = {
  create: (data) => api.post('/recurring-bookings', data),
  getForCalendar: (calendarId) => api.get(`/recurring-bookings/calendar/${calendarId}`),
  cancel: (id) => api.delete(`/recurring-bookings/${id}`),
};

// Resources
export const resourceAPI = {
  getAll: () => api.get('/resources'),
  create: (data) => api.post('/resources', data),
  update: (id, data) => api.put(`/resources/${id}`, data),
  delete: (id) => api.delete(`/resources/${id}`),
};

// Users
export const userAPI = {
  getAll: () => api.get('/users'),
  getOne: (id) => api.get(`/users/${id}`),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/password', data),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  deleteUser: (id) => api.delete(`/users/${id}`),
};

// Teams
export const teamAPI = {
  getCurrent: () => api.get('/teams/current'),
  update: (data) => api.put('/teams/current', data),
  invite: (data) => api.post('/teams/invite', data),
  getAvailableUsers: () => api.get('/teams/available-users'),
  updateMember: (userId, data) => api.put(`/teams/members/${userId}`, data),
  removeMember: (userId) => api.delete(`/teams/members/${userId}`),
  getInvitations: () => api.get('/teams/invitations'),
  cancelInvitation: (inviteId) => api.delete(`/teams/invitations/${inviteId}`),
  resendInvitation: (inviteId) => api.post(`/teams/invitations/${inviteId}/resend`),
};

// Invitations (public)
export const invitationAPI = {
  getDetails: (token) => api.get(`/invitations/${token}`),
  accept: (token, data) => api.post(`/invitations/${token}/accept`, data),
};

// Organizations
export const organizationAPI = {
  getCurrent: () => api.get('/organizations/current'),
  update: (data) => api.put('/organizations/current', data),
  getPublic: (userId) => api.get(`/organizations/public/${userId}`),
  addMember: (data) => api.post('/organizations/members', data),
  updateMember: (userId, data) => api.put(`/organizations/members/${userId}`, data),
  removeMember: (userId) => api.delete(`/organizations/members/${userId}`),
  delete: () => api.delete('/organizations/current'),
  getStats: () => api.get('/organizations/stats'),
  getMembers: () => api.get('/organizations/members'),
  getSettings: () => api.get('/organizations/settings'),
};

// Billing
export const billingAPI = {
  getSubscription: () => api.get('/billing/subscription'),
  subscribe: (data) => api.post('/billing/subscribe', data),
  cancelSubscription: () => api.delete('/billing/subscription'),
  getInvoices: () => api.get('/billing/invoices'),
  getStats: () => api.get('/billing/stats'),
};

// Notifications
export const notificationAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

export const googleCalendarAPI = {
  getAuthUrl: () => api.get('/google-calendar/auth-url'),
  callback: (code, state) => api.post('/google-calendar/callback', { code, state }),
  status: () => api.get('/google-calendar/status'),
  disconnect: () => api.post('/google-calendar/disconnect'),
};

export const outlookCalendarAPI = {
  getAuthUrl: () => api.get('/outlook-calendar/auth-url'),
  callback: (code, state) => api.post('/outlook-calendar/callback', { code, state }),
  status: () => api.get('/outlook-calendar/status'),
  disconnect: () => api.post('/outlook-calendar/disconnect'),
};

export const googleBusinessAPI = {
  status: () => api.get('/google-business/status'),
  connect: (data) => api.post('/google-business/connect', data),
  disconnect: () => api.post('/google-business/disconnect'),
  getReviews: () => api.get('/google-business/reviews'),
  updateReviewNote: (reviewId, data) => api.post(`/google-business/reviews/${reviewId}/note`, data),
  deleteReviewNote: (reviewId) => api.delete(`/google-business/reviews/${reviewId}/note`),
};

export const emailTemplateAPI = {
  getAll: () => api.get('/email-templates'),
  preview: (id) => api.get(`/email-templates/${id}/preview`),
  sendTest: (id) => api.post(`/email-templates/${id}/test`),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
};

export const personalizationAPI = {
  get: () => api.get('/personalization'),
  update: (data) => api.put('/personalization', data),
  getPublic: (userId) => api.get(`/personalization/public/${userId}`),
};

export const publicProfileAPI = {
  getAll: (params) => api.get('/public-profiles', { params }),
  getById: (id) => api.get(`/public-profiles/${id}`),
};

export const posAPI = {
  getTransactions: (params) => api.get('/pos/transactions', { params }),
  getStats: () => api.get('/pos/stats'),
  refund: (id) => api.post(`/pos/refund/${id}`),
  squareRefund: (id, data) => api.post(`/pos/square/refund/${id}`, data),
  createSquareTerminalCheckout: (data) => api.post('/pos/square/terminal/checkout', data),
  getSquareTerminalStatus: (checkoutId) => api.get(`/pos/square/terminal/status/${checkoutId}`),
  pairCloverDevice: (data) => api.post('/pos/clover/device/pair', data),
  chargeCloverDevice: (data) => api.post('/pos/clover/device/charge', data),
  getCloverDeviceStatus: () => api.get('/pos/clover/device/status'),
};

export const taxAPI = {
  getRates: () => api.get('/tax/rates'),
  getSummary: () => api.get('/tax/summary'),
  createRate: (data) => api.post('/tax/rates', data),
  deleteRate: (id) => api.delete(`/tax/rates/${id}`),
  calculate: (data) => api.post('/tax/calculate', data),
  getDefaults: () => api.get('/tax/defaults'),
  getReport: (params) => api.get('/tax/report', { params }),
};

export const resellerAPI = {
  apply: (data) => api.post('/resellers/apply', data),
  getProfile: () => api.get('/resellers/me'),
  getClients: () => api.get('/resellers/clients'),
  createClient: (data) => api.post('/resellers/clients', data),
  getDashboard: () => api.get('/resellers/dashboard'),
  generateApiKey: () => api.post('/resellers/api-key'),
  getApiKey: () => api.get('/resellers/api-key'),
};

export const agencyAPI = {
  createSubOrganization: (data) => api.post('/agency/sub-organization', data),
  getSubOrganizations: () => api.get('/agency/sub-organizations'),
  getDashboard: () => api.get('/agency/dashboard'),
  updateSubOrganization: (id, data) => api.put(`/agency/sub-organization/${id}`, data),
  deleteSubOrganization: (id) => api.delete(`/agency/sub-organization/${id}`),
};

export const forecastingAPI = {
  getDemand: (calendarId) => api.get('/forecasting/demand', { params: { calendarId } }),
};

export const paymentGatewayAPI = {
  getConfig: () => api.get('/payment-gateway/config'),
  updateConfig: (data) => api.put('/payment-gateway/config', data),
  clearProvider: (provider) => api.delete(`/payment-gateway/config/${provider}`),
};

export const disputesAPI = {
  getAll: () => api.get('/disputes'),
  getOne: (id) => api.get(`/disputes/${id}`),
  submitEvidence: (id, evidence) => api.post(`/disputes/${id}/evidence`, evidence),
  close: (id) => api.post(`/disputes/${id}/close`),
};

export const customDomainsAPI = {
  verify: (domain) => api.post('/custom-domains/verify', { domain }),
  updateBranding: (data) => api.put('/custom-domains/branding', data),
  remove: () => api.delete('/custom-domains'),
};

export const referralAPI = {
  getMyCode: () => api.get('/referrals/my-code'),
  applyCode: (code) => api.post('/referrals/apply', { code }),
  getStats: () => api.get('/referrals/stats'),
  complete: (id) => api.post(`/referrals/${id}/complete`),
};

export default api;
